import React, { useEffect, useRef, useState } from 'react';
import { Modifier } from '../../../../../lib/bemUtils/Modifier';
import { useTranslation } from 'react-i18next';
import { createPopper } from '@popperjs/core';
// Components
import FormMultiselectPanel, { FormMultiselectPanelModifier } from './FormMultiselectPanel/FormMultiselectPanel';
import { Namespace } from '../../../../../translation/namespace';
import bem from '../../../../../lib/bemUtils/bem';
import {
  CollapsibleContent,
  CollapsibleOverlayModifier,
} from '../../../../organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../../../hooks/useCollapsibleOverlay';
import { StateFacetItem } from '../../../ResultListSettings/ResultListSettings';
import { HeadingHierarchy } from '../../../../../_lib/components/Heading/Heading';
import { FilterButton, FilterButtonModifier } from '../../../ResultListSettings/partials/FilterButton';

type FormMultiselectType = {
  title: string;
  name: string;
  buttonHeadline?: string;
  buttonModifier?: Modifier<FilterButtonModifier>;
  collapsibleModifier?: Modifier<CollapsibleOverlayModifier>;
  panelModifier?: Modifier<FormMultiselectPanelModifier>;
  modifier?: Modifier<FormMultiselectModifier>;
  hierarchy?: HeadingHierarchy;
  showButtons?: boolean;
  focusOnMount?: boolean;
  items?: StateFacetItem[];
  disableSubmitButton?: boolean;
  // Events
  onSubmit?: any;
  onChange?: any;
  // for Resultlistsettings where this is not handled by formmodel
  onCloseResetFacet?: any;
  sortAphabetically?: boolean;
};

export enum FormMultiselectModifier {
  WIDTH_100 = 'width-100',
  SELECT_A_LOT = 'select-a-lot',
}

/**
 * FormMultiselect
 */
export default function FormMultiselect({
  buttonHeadline,
  buttonModifier,
  collapsibleModifier,
  panelModifier,
  focusOnMount = false,
  hierarchy,
  disableSubmitButton = false,
  items = [],
  name,
  onChange,
  onSubmit,
  showButtons = false,
  title,
  onCloseResetFacet,
  sortAphabetically = false,
  modifier,
}: FormMultiselectType) {
  const { t } = useTranslation();
  const { collapsibleOpen, focusToggle, toggleCollapsible, toggleProps, collapsibleProps } = useCollapsibleOverlay();

  // Creating snapshot of selection "on load" to restore this on reset
  const initialSnapshotRef = useRef(JSON.parse(JSON.stringify(items)));

  // We have to parse it, in other case dynamicItems gets same reference
  const [dynamicItems, setDynamicItems] = useState(JSON.parse(JSON.stringify(items)));

  const getActiveItems = () =>
    showButtons ? items.filter(item => item.active) : dynamicItems.filter((item: StateFacetItem) => item.active);
  const hasActiveItems = () => getActiveItems().length > 0;

  /**
   * Returns the Containers-Title according to the active items.
   */
  const getActiveItemsTitle = () => {
    const activeItems = getActiveItems();
    switch (activeItems.length) {
      case 0:
        return '';
      case 1:
        return `${activeItems[0].text}`;
      default:
        return `${activeItems.length} ${t(`${Namespace.glossary}:active`)}`;
    }
  };

  // is held in state, so it won't change while we select/deselect items
  const [activeItemTitle, setActiveItemTitle] = useState(getActiveItemsTitle());

  /**
   * Filter change's event handler
   * Mutliselects without submit buttons show dynamic change of button title, when options are selected.
   * @param event single check box event providing data what to add to filter
   */
  const handleOnFilterChange = (event: any) => {
    if (onChange) {
      onChange(event);
    }
    const currentDynamicItems = JSON.parse(JSON.stringify(dynamicItems));
    const index = currentDynamicItems.findIndex((item: StateFacetItem) => item.value === event.target.value);
    currentDynamicItems[index].active = event.target.checked;
    setDynamicItems(currentDynamicItems);
  };

  /**
   * Submit button's event handler
   * @param event
   */
  const handleOnSubmit = (event: any) => {
    if (onSubmit) {
      onSubmit(event, getActiveItems(), name);
    }
  };

  const handleOnReset = () => {
    setDynamicItems(initialSnapshotRef.current);
    toggleCollapsible();
    focusToggle();
  };

  /**
   *  change button title onChange only if MultiSelect can't be submitted (no submit buttons here)
   */
  useEffect(() => {
    if (!showButtons) {
      setActiveItemTitle(getActiveItemsTitle());
    }
  }, [dynamicItems]);

  /**
   * handle rendering of collapsible via popper
   */
  useEffect(() => {
    let popper: any;

    if (collapsibleOpen) {
      // using timeout here to tackle a race condition between popper and the Browser's popover API
      setTimeout(() => {
        popper = createPopper(
          toggleProps.ref.current as any,
          document.getElementById(collapsibleProps.id) as HTMLElement,
          {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 1],
                },
              },
            ],
          }
        );
      }, 0);
    }

    // special case where values need to be rest.
    // not handled by formModel magic -> see ResultListSettings.
    if (!collapsibleOpen && onCloseResetFacet) {
      onCloseResetFacet();
    }

    return () => {
      if (popper) {
        popper.destroy();
      }
    };
    // onCloseResetFacet is not needed here, else it can't be reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsibleOpen, toggleProps.ref]);

  // onMount
  useEffect(() => {
    if (focusOnMount) {
      focusToggle();
    }
    // only want on mount, so it's ok here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={bem('form-multiselect', modifier)}>
      <FilterButton
        title={title}
        value={activeItemTitle}
        isActive={hasActiveItems()}
        modifier={buttonModifier}
        buttonProps={{
          ...toggleProps,
          // Usually, there are items. In some cases, backend has none, so we disable the button, see SON-19586
          disabled: !items || items.length === 0 ? true : undefined,
        }}
      />
      <CollapsibleContent
        {...collapsibleProps}
        role="dialog"
        aria-label={title}
        returnChildrenWhenClosed
        modifier={collapsibleModifier}
      >
        <FormMultiselectPanel
          name={name}
          items={dynamicItems}
          buttonHeadline={buttonHeadline}
          hierarchy={hierarchy}
          showButtons={showButtons}
          controls={name}
          disableSubmitButton={disableSubmitButton}
          // Events
          onSubmit={handleOnSubmit}
          onChange={handleOnFilterChange}
          onReset={handleOnReset}
          sortAlphabetically={sortAphabetically}
          modifier={panelModifier}
        />
      </CollapsibleContent>
    </div>
  );
}
